/**
 * Disziplin Sprung: ein einziger Wisch, gemessen wird, wie weit die Bahn
 * insgesamt laeuft - Ausrollen inklusive.
 *
 * Die eigentliche Regel steckt in JumpAttempt. Dieser Screen zeigt nur an, in
 * welcher Phase der Versuch gerade ist.
 */

import { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import { Header } from '../components/Header'
import { LiveValue } from '../components/LiveValue'
import { ResultPanel } from '../components/ResultPanel'
import { ScrollTrack } from '../components/ScrollTrack'
import type { TapeMarker } from '../components/drawTape'
import { useDevice } from '../DeviceContext'
import { JumpAttempt } from '../engine/disciplines'
import { loadRecords } from '../engine/records'
import type { ScrollTracker } from '../engine/scrollTracker'
import { formatDistance, formatDistanceInline } from '../engine/units'

export interface DisciplineChallengeProps {
  /** Gesetzt, wenn der Versuch die Antwort auf ein Duell ist. */
  challengeCode?: string
  /** Weite des Herausforderers in Zentimetern, als Marke auf der Bahn. */
  rivalCm?: number
  rivalName?: string
  onAnswered?: () => void
}

export function SprungScreen({
  challengeCode,
  rivalCm,
  rivalName,
  onAnswered,
}: DisciplineChallengeProps = {}) {
  const { metrics } = useDevice()
  const trackerRef = useRef<ScrollTracker | null>(null)
  const [attempt, setAttempt] = useState<JumpAttempt | null>(null)
  const [, rerender] = useReducer((count: number) => count + 1, 0)

  const handleTracker = useCallback(
    (tracker: ScrollTracker) => {
      trackerRef.current = tracker
      setAttempt(new JumpAttempt(tracker, rerender))
    },
    [rerender],
  )

  const phase = attempt?.phase ?? 'bereit'

  // Die persoenliche Bestweite als Ziellinie auf der Bahn. Ohne sichtbare Marke
  // wuesste niemand, ob der Sprung gut war, bevor er zu Ende ist. Nach jedem
  // gewerteten Versuch neu gelesen, damit ein frischer Rekord sofort steht.
  const markers = useMemo<TapeMarker[]>(() => {
    const result: TapeMarker[] = []
    if (rivalCm && rivalCm > 0) {
      result.push({ cm: rivalCm, label: `${rivalName ?? 'Gegner'} ${formatDistanceInline(rivalCm)}` })
    }
    const best = loadRecords().best['sprung']
    if (best && best.distanceCm > 0) {
      result.push({ cm: best.distanceCm, label: `Bestweite ${formatDistanceInline(best.distanceCm)}` })
    }
    return result
  }, [phase, rivalCm, rivalName])

  const liveDistance = useCallback(() => {
    const cm = trackerRef.current?.forwardCm ?? 0
    const formatted = formatDistance(cm)
    return `${formatted.value} ${formatted.unit}`
  }, [])

  return (
    <div className="screen" data-discipline="sprung">
      <Header title="Sprung" />
      <ScrollTrack metrics={metrics} onTracker={handleTracker} markers={markers} referenceRatio={0.22}>
        {phase === 'bereit' && (
          <div className="hud hud--center">
            <p className="hud__hint">Einmal kräftig nach oben wischen und loslassen.</p>
            <p className="hud__note">Das Nachrollen zählt mit. Anfassen währenddessen ist Übertreten.</p>
          </div>
        )}

        {(phase === 'anlauf' || phase === 'flug') && (
          <div className="hud hud--center">
            <LiveValue get={liveDistance} className="hud__value" />
            <p className="hud__hint">{phase === 'anlauf' ? 'Anlauf' : 'Flug…'}</p>
          </div>
        )}

        {phase === 'uebertreten' && (
          <div className="panel panel--foul">
            <h2 className="panel__title">Übertreten</h2>
            <p>
              Du hast die Bahn berührt, während sie noch ausgerollt ist. Der Versuch ist ungültig –
              das Anfassen bremst die Bahn und würde jede Messung wertlos machen.
            </p>
            <button className="button button--primary" type="button" onClick={() => attempt?.retry()}>
              Neuer Versuch
            </button>
          </div>
        )}

        {phase === 'gemessen' && attempt && (
          <ResultPanel
            result={attempt.toResult()}
            headline={formatDistanceInline(attempt.resultCm)}
            subline={`davon ${Math.round(attempt.flightShare * 100)} % im Nachrollen`}
            challengeCode={challengeCode}
            onChallengeAnswered={onAnswered}
            onRetry={() => attempt.retry()}
          />
        )}
      </ScrollTrack>
    </div>
  )
}
